// PaymentPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const MONTH_NAMES = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export function PaymentPage() {
  const { member } = useAuth();
  const [step, setStep] = useState(1); // 1=UTR+amount, 2=allocations, 3=success
  const [utr, setUtr] = useState('');
  const [amount, setAmount] = useState('');
  const [members, setMembers] = useState([]);
  const [allocations, setAllocations] = useState([
    { id: 1, member_id: '', allocation_type: 'fees', custom_amount: '', note: '',
      target_type: null, target_id: null, target_label: null }
  ]);
  const [submitting, setSubmitting] = useState(false);

  // Phase 1 (c.1): per-member caches for "Customise" picker + advance credit display.
  // Lazy-loaded — only fetched when a member is selected for an allocation.
  const [unpaidByMember, setUnpaidByMember] = useState({});   // memberId -> { fees: [], fines: [] }
  const [creditByMember, setCreditByMember] = useState({});   // memberId -> rupees
  const [customisingFor, setCustomisingFor] = useState(null); // allocation.id when modal open

  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  useEffect(() => {
    api.get('/members').then(r => {
      const all = (r.data.members || []).filter(m => !m.is_observer);
      setMembers(all);
      // Default first allocation to self
      if (member?.id) {
        setAllocations([{ id: 1, member_id: member.id, allocation_type: 'fees',
          custom_amount: '', note: '',
          target_type: null, target_id: null, target_label: null }]);
      }
    });
  }, [member?.id]);

  // Lazy-fetch unpaid items + advance credit for any beneficiary the user picks.
  // Cached per member_id so changing allocations between the same members is instant.
  useEffect(() => {
    const memberIds = Array.from(new Set(allocations.map(a => a.member_id).filter(Boolean)));
    for (const mid of memberIds) {
      if (!unpaidByMember[mid]) {
        api.get('/payments/my/unpaid', { params: { member_id: mid } })
          .then(r => setUnpaidByMember(prev => ({ ...prev, [mid]: r.data })))
          .catch(() => setUnpaidByMember(prev => ({ ...prev, [mid]: { fees: [], fines: [] } })));
      }
      if (creditByMember[mid] === undefined) {
        api.get('/payments/credits/my', { params: { member_id: mid } })
          .then(r => setCreditByMember(prev => ({ ...prev, [mid]: parseFloat(r.data.advance_credit || 0) })))
          .catch(() => setCreditByMember(prev => ({ ...prev, [mid]: 0 })));
      }
    }
    // Note: deliberately depending on a derived string of member IDs rather than
    // the unpaidByMember / creditByMember caches themselves — including those
    // would cause infinite re-fetch loops since we set them inside this effect.
  }, [allocations.map(a => a.member_id).join('|')]);

  const totalAmount = parseFloat(amount) || 0;
  const allocatedTotal = allocations.reduce((s, a) => s + (parseFloat(a.custom_amount) || 0), 0);
  const unallocated = totalAmount - allocatedTotal;
  const isBalanced = Math.abs(unallocated) < 0.01;

  function addAllocation() {
    setAllocations(prev => [...prev, {
      id: Date.now(), member_id: '', allocation_type: 'fees', custom_amount: '', note: '',
      target_type: null, target_id: null, target_label: null
    }]);
  }

  function removeAllocation(id) {
    setAllocations(prev => prev.filter(a => a.id !== id));
  }

  function updateAllocation(id, field, value) {
    setAllocations(prev => prev.map(a => {
      if (a.id !== id) return a;
      const next = { ...a, [field]: value };
      // Changing member or allocation_type clears any previously-picked target
      // (it would no longer apply to the new member or type).
      if (field === 'member_id' || field === 'allocation_type') {
        next.target_type = null;
        next.target_id = null;
        next.target_label = null;
      }
      return next;
    }));
  }

  function setAllocationTarget(allocId, targetType, targetId, label) {
    setAllocations(prev => prev.map(a => a.id === allocId
      ? { ...a, target_type: targetType, target_id: targetId, target_label: label }
      : a));
  }

  function clearAllocationTarget(allocId) {
    setAllocations(prev => prev.map(a => a.id === allocId
      ? { ...a, target_type: null, target_id: null, target_label: null }
      : a));
  }

  function fillRemaining(id) {
    const others = allocations.filter(a => a.id !== id).reduce((s, a) => s + (parseFloat(a.custom_amount) || 0), 0);
    const remaining = totalAmount - others;
    if (remaining > 0) updateAllocation(id, 'custom_amount', remaining.toString());
  }

  async function handleSubmit() {
    if (!utr.trim()) return alert('UTR number is required');
    if (!totalAmount || totalAmount <= 0) return alert('Enter a valid amount');
    if (!isBalanced) return alert('Allocation total (₹' + allocatedTotal.toLocaleString('en-IN') + ') must equal payment amount (₹' + totalAmount.toLocaleString('en-IN') + ')');
    for (const a of allocations) {
      if (!a.member_id) return alert('Select a member for each allocation');
      if (!parseFloat(a.custom_amount) || parseFloat(a.custom_amount) <= 0) return alert('Enter amount for each allocation');
    }

    setSubmitting(true);
    try {
      // Submit one payment_submission per allocation line
      for (const a of allocations) {
        await api.post('/payments/submit', {
          utr: utr.trim().toUpperCase() + (allocations.length > 1 ? '_SPLIT_' + a.id : ''),
          amount: parseFloat(a.custom_amount),
          beneficiary_member_id: a.member_id,
          allocation_type: a.allocation_type,
          allocation_note: a.note || (allocations.length > 1 ? 'Split payment from UTR ' + utr.trim().toUpperCase() : null),
          // Phase 1 (c.1): pass member-picked target if set
          target_type: a.target_type || null,
          target_id:   a.target_id   || null,
          is_partial: false,
          original_utr: utr.trim().toUpperCase(),
        });
      }
      setStep(3);
    } catch (err) {
      alert(err.response?.data?.error || 'Submission failed');
    } finally { setSubmitting(false); }
  }

  // ── STEP 3: Success ──
  if (step === 3) {
    return (
      <div style={{ background:'#f4f6fb', minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
        <div style={{ background:'white', borderRadius:16, padding:32, textAlign:'center', maxWidth:360, width:'100%', boxShadow:'0 4px 24px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize:'3rem', marginBottom:16 }}>✅</div>
          <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'1.4rem', color:'#1E3A6E', marginBottom:8 }}>Payment Submitted!</div>
          <div style={{ fontSize:'0.85rem', color:'#6b7280', marginBottom:4 }}>
            UTR: <strong style={{ fontFamily:'monospace' }}>{utr.toUpperCase()}</strong>
          </div>
          <div style={{ fontSize:'0.85rem', color:'#6b7280', marginBottom:12 }}>
            Amount: <strong>₹{totalAmount.toLocaleString('en-IN')}</strong>
            {allocations.length > 1 && <span> split across {allocations.length} members</span>}
          </div>
          {allocations.length > 1 && (
            <div style={{ background:'#f8fafc', borderRadius:8, padding:10, marginBottom:12, textAlign:'left' }}>
              {allocations.map((a, i) => {
                const m = members.find(x => x.id === a.member_id);
                return (
                  <div key={a.id} style={{ display:'flex', justifyContent:'space-between', fontSize:'0.8rem', padding:'3px 0', borderBottom: i < allocations.length-1 ? '1px solid #f3f4f6' : 'none' }}>
                    <span style={{ color:'#374151' }}>{m?.display_name || '?'} · {a.allocation_type}</span>
                    <span style={{ fontWeight:700, color:'#1E3A6E' }}>₹{parseFloat(a.custom_amount).toLocaleString('en-IN')}</span>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:10, marginBottom:20, fontSize:'0.8rem', color:'#16a34a' }}>
            Will be auto-matched when bank statement is uploaded. You'll be notified.
          </div>
          <button onClick={() => window.location.href = '/'}
            style={{ width:'100%', padding:'12px', borderRadius:10, border:'none', background:'#1E3A6E', color:'white', fontWeight:700, fontSize:'0.95rem', cursor:'pointer', fontFamily:'Rajdhani,sans-serif' }}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background:'#f4f6fb', minHeight:'100vh', paddingBottom:80 }}>

      {/* Header */}
      <div style={{ background:'white', borderBottom:'1px solid #e5e7eb', padding:'18px 16px 14px' }}>
        <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'1.4rem', color:'#1E3A6E' }}>💳 Record Payment</div>
        <div style={{ fontSize:'0.82rem', color:'#6b7280' }}>Transfer to UCC account, then submit your UTR here</div>
      </div>

      <div style={{ padding:'16px' }}>

        {/* Bank account card */}
        <div style={{ background:'#1E3A6E', borderRadius:14, padding:'14px 16px', marginBottom:16, border:'2px solid #F5C518' }}>
          <div style={{ fontSize:'0.65rem', color:'#F5C518', textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:10, textAlign:'center' }}>
            🏦 UCC Bank Account
          </div>
          {[
            { label:'Account Name', value:'United Cricket Club' },
            { label:'Account Number', value:'100175390927', mono:true },
            { label:'IFSC Code', value:'INDB0000163', mono:true },
            { label:'Transfer Mode', value:'NEFT / IMPS / UPI' },
          ].map(r => (
            <div key={r.label} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid rgba(255,255,255,.1)' }}>
              <span style={{ fontSize:'0.72rem', color:'rgba(255,255,255,.55)' }}>{r.label}</span>
              <span style={{ fontSize:'0.85rem', fontWeight:700, fontFamily: r.mono ? 'monospace' : 'inherit', color:'white' }}>{r.value}</span>
            </div>
          ))}
          <div style={{ marginTop:10, background:'rgba(245,197,24,.12)', borderRadius:8, padding:'7px 10px', fontSize:'0.75rem', color:'rgba(255,255,255,.75)', textAlign:'center' }}>
            After transfer, copy UTR from your bank app and submit below ↓
          </div>
        </div>

        {/* UTR + Amount */}
        <div style={{ background:'white', borderRadius:12, padding:16, marginBottom:12, border:'1px solid #e5e7eb' }}>
          <div style={{ fontWeight:700, fontSize:'0.8rem', color:'#374151', marginBottom:12, textTransform:'uppercase', letterSpacing:'0.06em' }}>
            Step 1 — Enter UTR & Amount
          </div>
          <div className="form-group">
            <label className="form-label">UTR / Reference Number *</label>
            <input type="text" className="form-control"
              placeholder="e.g. ICICI2603458901"
              value={utr} onChange={e => setUtr(e.target.value.toUpperCase().replace(/\s/g, ''))}
              style={{ fontFamily:'monospace', letterSpacing:'0.05em' }}
              autoCapitalize="characters" />
            <div style={{ fontSize:'0.72rem', color:'#9ca3af', marginTop:4 }}>Find in your UPI / banking app transaction history</div>
          </div>
          <div className="form-group" style={{ marginBottom:0 }}>
            <label className="form-label">Total Amount Transferred (₹) *</label>
            <input type="number" className="form-control"
              placeholder="e.g. 10000"
              value={amount} onChange={e => { setAmount(e.target.value); }}
              min="1" inputMode="numeric" style={{ fontSize:'1.1rem', fontWeight:700 }} />
          </div>
        </div>

        {/* Allocations */}
        {totalAmount > 0 && (
          <div style={{ background:'white', borderRadius:12, padding:16, marginBottom:12, border:'1px solid #e5e7eb' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
              <div style={{ fontWeight:700, fontSize:'0.8rem', color:'#374151', textTransform:'uppercase', letterSpacing:'0.06em' }}>
                Step 2 — Split Allocation
              </div>
              <div style={{ fontSize:'0.75rem', color: isBalanced ? '#16a34a' : unallocated > 0 ? '#d97706' : '#dc2626', fontWeight:700 }}>
                {isBalanced ? '✅ Balanced' : unallocated > 0 ? `₹${unallocated.toLocaleString('en-IN')} unallocated` : `₹${Math.abs(unallocated).toLocaleString('en-IN')} over-allocated`}
              </div>
            </div>

            {/* Visual split bar */}
            {allocations.length > 1 && totalAmount > 0 && (
              <div style={{ display:'flex', borderRadius:6, overflow:'hidden', height:8, marginBottom:14 }}>
                {allocations.map((a, i) => {
                  const pct = (parseFloat(a.custom_amount) || 0) / totalAmount * 100;
                  const colors = ['#1E3A6E','#F5C518','#16a34a','#dc2626','#7c3aed','#0891b2'];
                  return pct > 0 ? <div key={a.id} style={{ width: pct + '%', background: colors[i % colors.length] }} /> : null;
                })}
                {unallocated > 0 && <div style={{ flex:1, background:'#e5e7eb' }} />}
              </div>
            )}

            {allocations.map((alloc, idx) => {
              const memberName = members.find(m => m.id === alloc.member_id)?.display_name;
              const colors = ['#1E3A6E','#F5C518','#16a34a','#dc2626','#7c3aed','#0891b2'];
              return (
                <div key={alloc.id} style={{ borderRadius:10, border:'2px solid', borderColor: colors[idx % colors.length] + '40',
                  padding:12, marginBottom:10, background: colors[idx % colors.length] + '06' }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <div style={{ width:10, height:10, borderRadius:'50%', background: colors[idx % colors.length], flexShrink:0 }} />
                      <span style={{ fontSize:'0.78rem', fontWeight:700, color:'#374151' }}>
                        {idx === 0 ? 'Allocation 1' : 'Allocation ' + (idx+1)}
                      </span>
                    </div>
                    {allocations.length > 1 && (
                      <button onClick={() => removeAllocation(alloc.id)}
                        style={{ border:'none', background:'none', color:'#dc2626', fontSize:'1rem', cursor:'pointer', padding:'0 4px' }}>×</button>
                    )}
                  </div>

                  {/* Member */}
                  <div style={{ marginBottom:8 }}>
                    <label style={{ fontSize:'0.72rem', fontWeight:600, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.05em' }}>For Member</label>
                    <select className="form-control" style={{ marginTop:4 }}
                      value={alloc.member_id}
                      onChange={e => updateAllocation(alloc.id, 'member_id', e.target.value)}>
                      <option value="">Select member…</option>
                      {members.map(m => (
                        <option key={m.id} value={m.id}>{m.display_name} ({m.ucc_id})</option>
                      ))}
                    </select>
                  </div>

                  {/* Type + Amount row */}
                  <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                    <div style={{ flex:1 }}>
                      <label style={{ fontSize:'0.72rem', fontWeight:600, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.05em' }}>Towards</label>
                      <select className="form-control" style={{ marginTop:4 }}
                        value={alloc.allocation_type}
                        onChange={e => updateAllocation(alloc.id, 'allocation_type', e.target.value)}>
                        <option value="fees">💰 Fees</option>
                        <option value="fines">⚠️ Fine</option>
                        <option value="mixed">🔀 Mixed</option>
                      </select>
                    </div>
                    <div style={{ flex:1 }}>
                      <label style={{ fontSize:'0.72rem', fontWeight:600, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.05em' }}>Amount (₹)</label>
                      <div style={{ display:'flex', gap:4, marginTop:4 }}>
                        <input type="number" className="form-control" placeholder="0"
                          value={alloc.custom_amount} inputMode="numeric"
                          onChange={e => updateAllocation(alloc.id, 'custom_amount', e.target.value)}
                          style={{ fontWeight:700 }} />
                        {unallocated > 0 && (
                          <button onClick={() => fillRemaining(alloc.id)}
                            title="Fill remaining"
                            style={{ padding:'0 10px', borderRadius:8, border:'1px solid #e5e7eb', background:'#f9fafb', cursor:'pointer', fontSize:'0.75rem', color:'#374151', flexShrink:0, whiteSpace:'nowrap' }}>
                            +₹{unallocated.toLocaleString('en-IN')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Phase 1 (c.1): advance credit notice for the chosen beneficiary */}
                  {alloc.member_id && creditByMember[alloc.member_id] > 0 && (
                    <div style={{ background:'#fef3c7', border:'1px solid #fde68a', borderRadius:8,
                      padding:'7px 10px', marginBottom:8, fontSize:'0.74rem', color:'#92400e' }}>
                      💰 ₹{creditByMember[alloc.member_id].toLocaleString('en-IN')} advance credit · auto-applies when this payment is confirmed by admin
                    </div>
                  )}

                  {/* Phase 1 (c.1): customise target — pick a specific fee/fine */}
                  {alloc.member_id && (alloc.allocation_type === 'fees' || alloc.allocation_type === 'fines') && (
                    <div style={{ marginBottom:8 }}>
                      {alloc.target_type ? (
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                          background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:8, padding:'7px 10px' }}>
                          <div style={{ fontSize:'0.78rem', color:'#1d4ed8', fontWeight:600 }}>
                            🎯 {alloc.target_label}
                          </div>
                          <div style={{ display:'flex', gap:8 }}>
                            <button onClick={() => setCustomisingFor(alloc.id)}
                              style={{ background:'none', border:'none', color:'#1d4ed8',
                                fontSize:'0.74rem', cursor:'pointer', padding:0, textDecoration:'underline' }}>edit</button>
                            <button onClick={() => clearAllocationTarget(alloc.id)}
                              style={{ background:'none', border:'none', color:'#6b7280',
                                fontSize:'0.74rem', cursor:'pointer', padding:0, textDecoration:'underline' }}>clear</button>
                          </div>
                        </div>
                      ) : (
                        <button onClick={() => setCustomisingFor(alloc.id)}
                          style={{ background:'none', border:'none', color:'#6b7280', fontSize:'0.74rem',
                            cursor:'pointer', padding:0, textDecoration:'underline' }}>
                          Customise → pick a specific {alloc.allocation_type === 'fees' ? 'month' : 'fine'} to pay
                        </button>
                      )}
                    </div>
                  )}

                  {/* Note */}
                  <div>
                    <label style={{ fontSize:'0.72rem', fontWeight:600, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.05em' }}>Note (optional)</label>
                    <input type="text" className="form-control" style={{ marginTop:4 }}
                      placeholder={alloc.allocation_type === 'fees' ? 'e.g. March 2026 fees' : alloc.allocation_type === 'fines' ? 'e.g. Late fine 15 Mar' : 'Note…'}
                      value={alloc.note}
                      onChange={e => updateAllocation(alloc.id, 'note', e.target.value)} />
                  </div>
                </div>
              );
            })}

            {/* Add split button */}
            <button onClick={addAllocation}
              style={{ width:'100%', padding:'10px', borderRadius:10, border:'2px dashed #d1d5db',
                background:'white', color:'#6b7280', fontWeight:600, fontSize:'0.82rem',
                cursor:'pointer', marginTop:4 }}>
              + Add Another Allocation (Split Payment)
            </button>

            {/* Summary */}
            {allocations.length > 1 && (
              <div style={{ marginTop:12, background:'#f8fafc', borderRadius:8, padding:10 }}>
                <div style={{ fontSize:'0.72rem', fontWeight:700, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>Summary</div>
                {allocations.map((a, i) => {
                  const m = members.find(x => x.id === a.member_id);
                  const colors = ['#1E3A6E','#F5C518','#16a34a','#dc2626','#7c3aed','#0891b2'];
                  return (
                    <div key={a.id} style={{ display:'flex', justifyContent:'space-between', fontSize:'0.8rem', padding:'3px 0' }}>
                      <span style={{ color:'#374151' }}>
                        <span style={{ width:8, height:8, borderRadius:'50%', background:colors[i%colors.length], display:'inline-block', marginRight:6 }} />
                        {m?.display_name || 'Unknown'} · {a.allocation_type}
                        {a.target_label ? <span style={{ color:'#1d4ed8' }}> · 🎯 {a.target_label}</span> : ''}
                        {a.note ? <span style={{ color:'#9ca3af' }}> · {a.note}</span> : ''}
                      </span>
                      <span style={{ fontWeight:700, color: colors[i % colors.length] }}>₹{(parseFloat(a.custom_amount)||0).toLocaleString('en-IN')}</span>
                    </div>
                  );
                })}
                <div style={{ borderTop:'1px solid #e5e7eb', marginTop:6, paddingTop:6, display:'flex', justifyContent:'space-between', fontWeight:700, fontSize:'0.85rem' }}>
                  <span>Total</span>
                  <span style={{ color: isBalanced ? '#16a34a' : '#dc2626' }}>₹{allocatedTotal.toLocaleString('en-IN')} / ₹{totalAmount.toLocaleString('en-IN')}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Info */}
        <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:10, padding:'10px 12px', marginBottom:16, fontSize:'0.8rem', color:'#1d4ed8' }}>
          ℹ️ Your UTR will be automatically matched when the bank statement is uploaded. You'll receive a notification once confirmed.
        </div>

        <button onClick={handleSubmit} disabled={submitting || !utr || !totalAmount || (totalAmount > 0 && !isBalanced)}
          style={{ width:'100%', padding:'14px', borderRadius:12, border:'none',
            background: (!utr || !totalAmount || !isBalanced) ? '#e5e7eb' : '#1E3A6E',
            color: (!utr || !totalAmount || !isBalanced) ? '#9ca3af' : 'white',
            fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'1rem',
            letterSpacing:'0.08em', textTransform:'uppercase',
            cursor: (!utr || !totalAmount || !isBalanced) ? 'default' : 'pointer' }}>
          {submitting ? 'Submitting…' : isBalanced && totalAmount > 0 ? 'Submit Payment Record' : totalAmount > 0 ? 'Allocate Full Amount to Submit' : 'Enter Amount Above'}
        </button>
      </div>

      {/* Phase 1 (c.1): target picker modal — chooses a specific fee or fine */}
      {customisingFor && (() => {
        const alloc = allocations.find(a => a.id === customisingFor);
        if (!alloc) { setCustomisingFor(null); return null; }
        const data = unpaidByMember[alloc.member_id];
        const memberName = members.find(m => m.id === alloc.member_id)?.display_name || 'this member';
        const showFees  = (alloc.allocation_type === 'fees');
        const showFines = (alloc.allocation_type === 'fines');

        return (
          <div onClick={() => setCustomisingFor(null)}
            style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)',
              display:'flex', alignItems:'flex-end', justifyContent:'center', zIndex:50,
              paddingBottom:90, /* clear the persistent bottom nav (~80px) */
              animation:'fadein 0.15s ease-out' }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background:'white', borderRadius:'16px 16px 0 0', width:'100%',
                maxWidth:480, maxHeight:'80vh', overflowY:'auto', padding:'18px 16px 24px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                <div>
                  <div style={{ fontFamily:'Rajdhani,sans-serif', fontWeight:700, fontSize:'1.15rem', color:'#1E3A6E' }}>
                    Pick a specific {showFees ? 'month' : 'fine'}
                  </div>
                  <div style={{ fontSize:'0.78rem', color:'#6b7280', marginTop:2 }}>
                    For {memberName} · ₹{(parseFloat(alloc.custom_amount) || 0).toLocaleString('en-IN')} of this allocation
                  </div>
                </div>
                <button onClick={() => setCustomisingFor(null)}
                  style={{ background:'none', border:'none', fontSize:'1.4rem', color:'#6b7280', cursor:'pointer' }}>×</button>
              </div>

              {!data && (
                <div style={{ padding:'30px 0', textAlign:'center', color:'#9ca3af', fontSize:'0.85rem' }}>
                  Loading unpaid items…
                </div>
              )}

              {data && showFees && (
                <div>
                  {data.fees.length === 0 ? (
                    <div style={{ padding:'20px 0', textAlign:'center', color:'#9ca3af', fontSize:'0.85rem' }}>
                      No unpaid fees for this member 🎉
                    </div>
                  ) : data.fees.map(f => {
                    const label = MONTH_NAMES[f.month] + ' ' + f.year;
                    const balance = parseFloat(f.balance);
                    const due = parseFloat(f.amount_due);
                    const paid = parseFloat(f.amount_paid);
                    const isSelected = (alloc.target_id === f.id);
                    return (
                      <button key={f.id} onClick={() => {
                        setAllocationTarget(alloc.id, 'fee', f.id, label + ' fee · ₹' + balance.toLocaleString('en-IN') + ' due');
                        setCustomisingFor(null);
                      }}
                        style={{ width:'100%', textAlign:'left', padding:'12px 14px', borderRadius:10,
                          border: isSelected ? '2px solid #1E3A6E' : '1px solid #e5e7eb',
                          background: isSelected ? '#eff6ff' : 'white', marginBottom:8, cursor:'pointer',
                          display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                        <div>
                          <div style={{ fontWeight:700, fontSize:'0.92rem', color:'#1E3A6E' }}>💰 {label}</div>
                          <div style={{ fontSize:'0.76rem', color:'#6b7280', marginTop:2 }}>
                            {f.sessions_attended || 0} sessions · ₹{paid.toLocaleString('en-IN')} of ₹{due.toLocaleString('en-IN')} paid
                          </div>
                        </div>
                        <div style={{ fontWeight:700, fontSize:'1rem', color:'#dc2626' }}>
                          ₹{balance.toLocaleString('en-IN')}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {data && showFines && (
                <div>
                  {data.fines.length === 0 ? (
                    <div style={{ padding:'20px 0', textAlign:'center', color:'#9ca3af', fontSize:'0.85rem' }}>
                      No published unpaid fines for this member
                    </div>
                  ) : data.fines.map(fn => {
                    const balance = parseFloat(fn.balance);
                    const total = parseFloat(fn.amount);
                    const paid = parseFloat(fn.amount_paid);
                    const isSelected = (alloc.target_id === fn.id);
                    const issuedDate = fn.issued_at ? new Date(fn.issued_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '';
                    const reasonShort = (fn.reason || 'Fine').slice(0, 50);
                    return (
                      <button key={fn.id} onClick={() => {
                        setAllocationTarget(alloc.id, 'fine', fn.id, reasonShort + ' · ₹' + balance.toLocaleString('en-IN') + ' due');
                        setCustomisingFor(null);
                      }}
                        style={{ width:'100%', textAlign:'left', padding:'12px 14px', borderRadius:10,
                          border: isSelected ? '2px solid #1E3A6E' : '1px solid #e5e7eb',
                          background: isSelected ? '#eff6ff' : 'white', marginBottom:8, cursor:'pointer',
                          display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontWeight:700, fontSize:'0.9rem', color:'#1E3A6E', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                            ⚠️ {reasonShort}
                          </div>
                          <div style={{ fontSize:'0.74rem', color:'#6b7280', marginTop:2 }}>
                            Issued {issuedDate} · ₹{paid.toLocaleString('en-IN')} of ₹{total.toLocaleString('en-IN')} paid
                          </div>
                        </div>
                        <div style={{ fontWeight:700, fontSize:'1rem', color:'#dc2626', marginLeft:10 }}>
                          ₹{balance.toLocaleString('en-IN')}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              <div style={{ display:'flex', gap:8, marginTop:14 }}>
                {alloc.target_type && (
                  <button onClick={() => { clearAllocationTarget(alloc.id); setCustomisingFor(null); }}
                    style={{ flex:1, padding:'12px', borderRadius:10, border:'1px solid #e5e7eb',
                      background:'white', color:'#6b7280', fontWeight:600, fontSize:'0.85rem', cursor:'pointer' }}>
                    Clear (use oldest-first)
                  </button>
                )}
                <button onClick={() => setCustomisingFor(null)}
                  style={{ flex:1, padding:'12px', borderRadius:10, border:'none',
                    background:'#1E3A6E', color:'white', fontWeight:700, fontSize:'0.85rem',
                    cursor:'pointer', fontFamily:'Rajdhani,sans-serif' }}>
                  Done
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default PaymentPage;
